/* global performance */

/**
 * A burst blocking state tracker. This tracker function optimizes for both time
 * and importantly a maximum number of calls in a certain window. This is a
 * broader generalization than something like lodash's debounce because it
 * allows for a configurable number of events to happen within the window.
 * Debounce will always _only_ allow a single call within the timeframe.
 * Additionally, this tracker can be used in a broader scope than just a
 * function. The state tracking is decoupled from the action that is being
 * blocked.
 */
export default function makeBurstBlocker(
  options: {
   /**
    * Maximum calls allowed in `perMs` milliseconds
    */
    maxCalls: number,
    /**
     * Time period to measure number of calls in
     */
    perMs: number,
    /**
     * Dependency injection for tests
     */
    getTimestamp?: () => number,
  },
) {
  const {
    maxCalls,
    perMs,
    getTimestamp = () => performance.now(),
  } = options;

  let callTimestamps: number[] = [];

  return {
    isBlocked: (): boolean => {
      const currentTime = getTimestamp();
      callTimestamps.push(currentTime);

      const minTimestamp = currentTime - perMs;
      const callTimestampsInPerMs = callTimestamps.filter(timestamp => timestamp >= minTimestamp);
      const callsInPerMs = callTimestampsInPerMs.length;

      callTimestamps = callTimestampsInPerMs;

      // Tell caller that they are blocked when there have been more than maxCalls
      // calls to isBlocked() in the past `perMs` milliseconds.
      return callsInPerMs > maxCalls;
    },
    // Allow state to be reset for testing purposes
    reset: () => {
      callTimestamps = [];
    },
  };
}
